import { useSearch } from "@tanstack/react-router";
import { useGetIncomingPostbackQuery } from "api/TenantSlug/IncomingPostback/hooks.ts";
import { ModifyPostbackIncomingDrawer } from "components/Drawers/ModifyPostbackIncomingDrawer/ModifyPostbackIncomingDrawer.tsx";
import { TrackingToolFromGeneralForm } from "components/Forms/TrackingToolFromGeneralForm/TrackingToolFromGeneralForm.tsx";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { PermissionEnum, RoleEnum } from "types/enums.ts";
import { DrawerId, FormId } from "types/models.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getTrackingToolFromItems } from "utils/tenantHeaderItems.ts";

interface SearchParams {
    [`modifyIncomingPostback-id`]: string;
}

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/tracking/_layout/s2s-postback/_layout/$id/_layout/general/"
)({
    component: TrackingToolFromGeneralPage,
    staticData: {
        permissions: [PermissionEnum.ToolTrackingIncomingEditGeneral],
        showInSideMenu: false,
        label: "pages.tool.tracking.from.general.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.tracking.from.general.title"),
        };
    },
});

function TrackingToolFromGeneralPage() {
    const { id } = Route.useParams();
    const { data, isLoading, isError } = useGetIncomingPostbackQuery(id, {
        filter: {
            include: "types,sources,country,offers,excludedOffers,affiliates,advertisers",
        },
    });
    const incomingPostback = data?.incomingPostback;
    const userUtils = useUser();
    const formId = FormId.TrackingToolFromGeneralForm;
    const drawerId = DrawerId.ModifyIncomingPostback;
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const permissionsUtils = usePermission();

    if (!incomingPostback) {
        return (
            <QueryState
                isLoading={isLoading}
                isError={isError || !isLoading}
                errorKey="errors.loading_incoming_postback"
            />
        );
    }

    return (
        <TenantPage
            headerNavigationItems={getTrackingToolFromItems}
            isEdit
            saveFormId={
                userUtils
                    ?.getActiveTenant()
                    ?.userRoles?.some((role) => [RoleEnum.AdminFree, RoleEnum.AdminBeast].includes(role.name))
                    ? formId
                    : null
            }
        >
            <TrackingToolFromGeneralForm key={id} postback={incomingPostback} />
            {searchParams[`${drawerId}-id`] &&
                permissionsUtils.hasPermission(PermissionEnum.ToolTrackingIncomingPostbackExportView) && (
                    <ModifyPostbackIncomingDrawer />
                )}
        </TenantPage>
    );
}
